// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf
// with an alias.

// This is supposed to add IE11 support.
// Taken from docs-vuetify app.js
import 'babel-polyfill';
import 'event-source-polyfill';

// Safari on MacOS has no support for CanvasRenderingContext2D.filter,
// but we can polyfill most of that, see:
// https://www.npmjs.com/package/context-filter-polyfill
import 'context-filter-polyfill';

import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import relativeTime from 'dayjs/plugin/relativeTime';

import Vue from 'vue';
import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';

import * as Sentry from '@sentry/vue';
import {BrowserTracing} from '@sentry/tracing';

import bus from './bus';
import environment from './environment';
import router from './router';
import store from './store';

import API from './api';
import App from './App';
import i18n from './i18n';
import {getInitialBrowserLocale} from './i18n';
import socketbridge from './socketbridge.js';
import {
  getCurrentVuetifyDialog,
  closeCurrentVuetifyDialog,
} from '@/mixins/currentDialog';
import {desktopModeStylesFactory} from './mixins/desktopModeStyles.js';
import {longPollingRegistry} from './mixins/longPollingRegistry.js';

import IconTvLandscape from './components/IconTvLandscape';
import IconTvLandscapeSlaves from './components/IconTvLandscapeSlaves';
import IconTvPortrait from './components/IconTvPortrait';
import IconTvPortraitSlaves from './components/IconTvPortraitSlaves';
import IconTvSpecial from './components/IconTvSpecial';

Vue.use(API);
Vue.use(socketbridge);
Vue.use(Vuetify, {
  theme: {
    primary: '#ff665e', // aka 'signal-red'
    error: '#F21111',
  },
  icons: {
    // Don't use these directly, use IconTvDecoder instead.
    tv_landscape: {
      component: IconTvLandscape,
    },
    tv_landscape_slaves: {
      component: IconTvLandscapeSlaves,
    },
    tv_portrait: {
      component: IconTvPortrait,
    },
    tv_portrait_slaves: {
      component: IconTvPortraitSlaves,
    },
    tv_special: {
      component: IconTvSpecial,
    },
  },
});

Vue.filter('price', (number) => {
  let stringified = number;
  if (typeof number !== 'string') {
    stringified = number.toFixed(2);
  }
  if (stringified.endsWith('.00')) {
    stringified = stringified.slice(0, stringified.length - 3);
  }
  return stringified;
});

// Avoid dayjs defaulting to 'en' instead of browser/OS locale.
dayjs.locale(getInitialBrowserLocale());

/**
 * Allow "|dayjs('DD.MM.YYYY')" to format dates in template expressions.
 */
Vue.filter('dayjs', (date, formatString) => {
  return dayjs(date).format(formatString);
});

/**
 * Allow "|dayjsFromNow" to format dates in template expressions.
 */
dayjs.extend(relativeTime);
Vue.filter('dayjsFromNow', (date) => {
  return dayjs(date).fromNow();
});

/**
 * Make sure numbers (e.g. prices) use two digits.
 *
 * If not a number then just pass value unchanged.
 */
Vue.filter('digits', (val, numDigits) => {
  if (isNaN(val)) {
    // While this can falsely tell us that the value is-a-number,
    // it will not fail to detect the opposite. Example:
    //   isNaN(' ') === false -> well, that sucks
    //   isNaN([]) === false -> well, that sucks even more
    //   isNaN('100') === false -> this is fine
    //   isNaN('1, 2, 3') === true -> good! the check below would fail here
    return val;
  }
  // So the val may be a number, but to be sure we need an extra step
  // to avoid failing when e.g. given an empty string.
  const isNumber = !isNaN(parseFloat(val));
  return isNumber ? Number(val).toFixed(numDigits) : val;
});

/**
 * Add v-visible="..." directive to hide something while taking up space.
 *
 * Useful for table-like layouts where we may want to hide things
 * without affecting the layouting within a row.
 *
 * Based on:
 * https://github.com/javisperez/vue-visible/blob/master/src/v-visible.js
 */
Vue.directive('visible', (el, binding) => {
  el.style.visibility = !!binding.value ? 'visible' : 'hidden';
});

let sentryReplay = null;
if (environment.sentryDSN) {
  // https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/understanding-sessions/#manually-stopping-replay
  //   - replay.start();
  //   - await replay.stop();
  //   - await replay.flush();
  sentryReplay = new Sentry.Replay({
    maskAllText: false,
    maskAllInputs: false,
    blockAllMedia: false,
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/
    minReplayDuration: 15000,
  });

  // https://docs.sentry.io/platforms/javascript/guides/vue/
  Sentry.init({
    Vue: Vue,
    dsn: environment.sentryDSN,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        // https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/#tracepropagationtargets
        tracePropagationTargets: [
          'localhost',
          'cms.myshopradio.de',
          'cms-int.myshopradio.de',
          'marketingmanager.myshopradio.de',
          /^\//,
        ],
      }),
      // https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/privacy/
      sentryReplay,
    ],
    maxValueLength: 250 * 4,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // https://docs.sentry.io/platforms/javascript/guides/vue/#vue-specific-configuration
    trackComponents: true,
    attachProps: true,
  });

  Sentry.setContext('app', {
    facebookAppId: window.FACEBOOK_APP_ID || null,
  });

  console.log('Sentry installed');
}

Vue.config.productionTip = process.env.NODE_ENV !== 'production';
Vue.config.devtools = process.env.NODE_ENV !== 'production';
Vue.config.performance = process.env.NODE_ENV !== 'production';

// Setup a global eventbus.
Vue.prototype.$bus = bus;

// Setup a global mixin for shortcuts from any component.
Vue.mixin({
  computed: {
    $user() {
      return this.$store.state.common.currentUser;
    },
    $userprofile() {
      if (!this.$user) {
        return null;
      }
      return this.$user.clientprofile;
    },
    $desktopModeStyles() {
      return desktopModeStylesFactory(this);
    },
    $intervals() {
      return longPollingRegistry;
    },
  },
  methods: {
    /**
     * Close active dialog/drawer or navigate backwards.
     *
     * Callable from native WebViews (Android/iOS) via: window.app.<method>()
     */
    goBack() {
      // a) Close opened drawer when in mobile WebView.
      if (
        this.$vuetify.breakpoint.xs &&
        this.$store.state.common.isUsingWebview &&
        this.$store.state.common.showDrawer
      ) {
        this.$store.commit('SET_SHOW_DRAWER', false);
        return;
      }

      // b) Close any active Vuetify dialog.
      const dialogFound = closeCurrentVuetifyDialog();
      if (dialogFound) {
        return;
      }

      // c) Navigate backwards.
      history.back();
    },
    hasVuetifyDialogOpen() {
      return getCurrentVuetifyDialog() !== null;
    },
  },
});

/**
 * A directive to tack an element to a fixed y-position.
 *
 * Use like: <div v-tack="70">
 */
Vue.directive('tack', {
  bind(el, binding, vnode) {
    el.style.position = 'fixed';
    el.style.top = binding.value + 'px';
  },
});

/* eslint-disable no-new */
const app = new Vue({
  el: '#app',

  store,
  router,
  i18n,

  components: {App},
  template: '<App/>',
});

// Add a global reference to e.g. allow access in e2e tests and native WebViews.
window.app = app;
window.sentryReplay = sentryReplay;



// WEBPACK FOOTER //
// ./src/main.js